import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, MousePointer2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import HeroVideoGrid from "../HeroVideoGrid";
import MagneticButton from "../MagneticButton";

const EASE = [0.22, 1, 0.36, 1];

function StaggeredHeading({ words, accentIndices = [], testId }) {
  return (
    <h1
      data-testid={testId}
      className="font-display text-display-1 font-semibold text-ink"
      style={{ fontVariationSettings: '"opsz" 144' }}
    >
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: "0.4em" }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 + i * 0.08, ease: EASE }}
          className={
            "inline-block whitespace-pre " +
            (accentIndices.includes(i) ? "gold-foil-text" : "")
          }
        >
          {w}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </h1>
  );
}

export default function Hero({ onCtaClick }) {
  const { t, lang, isRTL } = useLanguage();

  // Build the stagger-able heading by joining the three keys with the accent in the middle.
  const pre = t("hero.title_pre");
  const accent = t("hero.title_accent");
  const post = t("hero.title_post");
  const preWords = pre.trim().split(/\s+/).filter(Boolean);
  const accentWord = accent.trim();
  const postWords = post.trim().split(/\s+/).filter(Boolean);
  const allWords = [...preWords, accentWord, ...postWords];
  const accentIdx = preWords.length;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-cream"
    >
      {/* Soft gold radial behind */}
      <div className="pointer-events-none absolute -top-40 end-[-10%] h-[640px] w-[640px] rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
      {/* Cheap CSS grain */}
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-50" aria-hidden="true" />

      <div className="container-page relative grid gap-10 lg:grid-cols-12 lg:gap-8 pt-14 pb-24 sm:pt-20 sm:pb-section-lg">
        {/* Text column */}
        <div className="lg:col-span-6 flex flex-col justify-center">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="eyebrow mb-7"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="h-3 w-3 text-gold" aria-hidden="true" />
            {t("hero.eyebrow")}
          </motion.span>

          <StaggeredHeading
            words={allWords}
            accentIndices={[accentIdx]}
            testId="hero-title"
          />

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-7 max-w-2xl text-body-lg text-ink/70"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <MagneticButton strength={12}>
              <button
                type="button"
                onClick={onCtaClick}
                className="btn-gold group"
                data-testid="hero-cta-primary"
              >
                <span>{t("hero.cta_primary")}</span>
                <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180" : ""}`} />
              </button>
            </MagneticButton>
            <a
              href={`/${lang}/works`}
              className="btn-outline-ink"
              data-testid="hero-cta-secondary"
            >
              {t("hero.cta_secondary")}
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.15 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-micro text-ink/55"
            data-testid="hero-trust-strip"
          >
            <span>{t("hero.trust_years")}</span>
            <span className="h-1 w-1 rounded-full bg-gold" aria-hidden="true" />
            <span>{t("hero.trust_projects")}</span>
            <span className="h-1 w-1 rounded-full bg-gold" aria-hidden="true" />
            <span>{t("hero.trust_languages")}</span>
          </motion.div>
        </div>

        {/* Visual column — animated video grid */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
          className="lg:col-span-6 flex items-center justify-center lg:justify-end min-h-[380px] lg:min-h-0 relative"
        >
          <HeroVideoGrid />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#services"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.4 }}
        className="hidden md:flex absolute bottom-6 start-1/2 -translate-x-1/2 items-center gap-2 text-micro text-ink/55 hover:text-ink transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <MousePointer2 className="h-3.5 w-3.5" />
        <span>{t("hero.scroll")}</span>
      </motion.a>
    </section>
  );
}
